import { useState, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowRight,
  ClipboardCheck,
  Layers,
  Plus,
  Ruler,
  Shapes,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import useConvertToSingle from '../useConvertToSingle';
import {
  selectV2,
  setMultiStage,
  setStep,
  startNextWorkpiece,
} from '../v2Upload.slice';
import { plural } from '../utils/fileHelpers';
import { HINT, STEP_HEADING } from '../components/fileCardStyles';
import { Confetti, SuccessMark } from '../components/submittedArt';
import SinglePieceConfirm from '../components/SinglePieceConfirm';

const CROWDED = 8;

function Cell({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex min-w-[180px] flex-1 items-center gap-3 px-5 py-1">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent text-primary">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block truncate font-primary text-lg font-semibold leading-tight">
          {value}
        </span>
        <span className="mt-1 block truncate text-[12px] text-muted-foreground">
          {label}
        </span>
      </span>
    </div>
  );
}

function ChoiceCard({
  focus,
  icon: Icon,
  title,
  body,
  action,
  onAct,
  variant,
}: {
  focus: string;
  icon: LucideIcon;
  title: string;
  body: string;
  action: string;
  onAct: () => void;
  variant?: 'outline';
}) {
  return (
    <div
      data-focus={focus}
      className="flex flex-col gap-3 rounded-xl border bg-card p-6 shadow-sm transition-[border-color,box-shadow] duration-fast hover:border-border-strong hover:shadow-md"
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-primary">
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="font-primary text-lg font-semibold text-heading">
        {title}
      </h3>
      <p className="text-[13px] leading-relaxed text-muted-foreground">
        {body}
      </p>
      <Button
        variant={variant}
        onClick={onAct}
        className="mt-auto self-start gap-2"
      >
        {action}
        <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

// fallow-ignore-next-line complexity
export default function WorkpieceCompleteStep() {
  const dispatch = useDispatch();
  const { basics, jobDetails, childIds, workpieceIndex, postId } =
    useSelector(selectV2);
  const convertToSingle = useConvertToSingle();
  const [confirming, setConfirming] = useState(false);

  const done = childIds.length;
  const alone = workpieceIndex === 1;
  const title = basics.title || `Workpiece ${workpieceIndex}`;
  const unit = jobDetails.units === 'Inches' ? 'in' : 'mm';
  const stock = jobDetails.thickness
    ? `${basics.material} · ${jobDetails.thickness} ${unit} thick`
    : basics.material;

  const addAnother = () => {
    dispatch(startNextWorkpiece());
    dispatch(setStep(1));
  };

  const finish = () => {
    if (alone) {
      setConfirming(true);
      return;
    }
    dispatch(setMultiStage('review'));
  };

  return (
    <div className="mx-auto flex w-full max-w-[1340px] flex-col gap-5">
      <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-success/[0.07] via-card to-success/[0.04] p-7">
        <Confetti />

        <div className="relative flex flex-col items-center gap-6 text-center lg:flex-row lg:text-left">
          <SuccessMark />
          <div className="min-w-0">
            <h2 className={STEP_HEADING}>
              Workpiece {workpieceIndex} is complete
            </h2>
            <p className="max-w-[560px] text-[15px] text-muted-foreground">
              &ldquo;{title}&rdquo; is saved. Nothing has gone to the reviewers
              yet — you name the whole project and submit it once the last
              workpiece is in.
            </p>
          </div>
        </div>

        <div className="relative mt-6 rounded-2xl border bg-card shadow-sm">
          <div className="flex flex-wrap items-center gap-y-4 divide-x py-4">
            <Cell
              icon={<Layers className="h-5 w-5" />}
              value={String(done)}
              label={`${plural(done, 'Workpiece', 'Workpieces')} complete`}
            />
            <Cell
              icon={<Shapes className="h-5 w-5" />}
              value={title}
              label="This workpiece"
            />
            <Cell
              icon={<Ruler className="h-5 w-5" />}
              value={stock}
              label="Material"
            />
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChoiceCard
          focus="add-workpiece"
          icon={Plus}
          title="Add Another Workpiece"
          body="A second piece with its own files, tools and job setup — so you walk the same six steps again from scratch. Nothing from this workpiece carries over."
          action={`Start workpiece ${workpieceIndex + 1}`}
          onAct={addAnother}
        />
        {alone ? (
          <ChoiceCard
            focus="finish-workpieces"
            icon={ClipboardCheck}
            title="Submit This One on Its Own"
            body="A multi-workpiece project needs at least two pieces, and you have one. This submits it as an ordinary single project instead — straight to its sign-off, no project details to fill in."
            action="Submit as a single project"
            onAct={finish}
            variant="outline"
          />
        ) : (
          <ChoiceCard
            focus="finish-workpieces"
            icon={ClipboardCheck}
            title="This is the Last Workpiece"
            body={`Next you name the whole project — title, description, cover image, license — and check over all ${done} workpieces. Then it goes to the reviewers as one submission.`}
            action="Name and review the project"
            onAct={finish}
          />
        )}
      </div>

      {done >= CROWDED && (
        <p className={cn(HINT, 'text-center')}>
          {done} workpieces is a lot for one project — you can still add more.
        </p>
      )}

      <SinglePieceConfirm
        open={confirming}
        onOpenChange={setConfirming}
        onConfirm={() => {
          convertToSingle(postId).catch(() => {});
        }}
      />
    </div>
  );
}
